import React, { lazy, Suspense, useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useVideoUrl } from '@/hooks/useVideoUrl';
import { Loader2 } from 'lucide-react';
import audioThumbnail from '@/assets/audio-thumbnail.png';

const LazyCustomVideoPlayer = lazy(() => import('@/utils/CustomVideoPlayer'));

interface VideoPlayerProps {
  recordingUrl?: string;
  thumbnailUrl?: string;
}

export const VideoPlayer = ({ recordingUrl, thumbnailUrl }: VideoPlayerProps) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [retryCount, setRetryCount] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);
  const [isAudio, setIsAudio] = useState(false);

  const { url, error } = useVideoUrl(recordingUrl);

  useEffect(() => {
    // Check if the recording is audio-only based on URL or extension
    if (recordingUrl) {
      const isAudioFile = recordingUrl.toLowerCase().endsWith('.mp3') ||
                        recordingUrl.toLowerCase().endsWith('.wav') ||
                        recordingUrl.toLowerCase().endsWith('.m4a');
      setIsAudio(isAudioFile);
    }
  }, [recordingUrl]);

  const handleRetry = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRetryCount(prev => prev + 1);
  };

  const handleThumbnailClick = () => {
    if (url && !error && !showPlayer) {
      setShowPlayer(true);
    }
  };

  // Determine which thumbnail to show
  const getThumbnailUrl = () => {
    if (isAudio) {
      return audioThumbnail;
    }
    return thumbnailUrl || '/placeholder-thumbnail.jpg';
  };

  return (
    <div 
      ref={ref} 
      className="relative w-full max-w-[640px] h-[360px] rounded-lg overflow-hidden"
      onClick={handleThumbnailClick}
    >
      {/* Thumbnail container */}
      <div 
        className={`absolute inset-0 w-full h-full ${!showPlayer ? 'cursor-pointer' : ''}`}
        style={{ 
          backgroundImage: `url(${getThumbnailUrl()})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />

      {/* Show play button overlay on thumbnail when not playing */}
      {inView && url && !showPlayer && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-40 transition-all">
          <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
            <svg width="30" height="30" viewBox="0 0 37 38" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M27.2122 18.3131C27.5308 18.5719 27.5308 19.0582 27.2122 19.317L15.236 29.0464C14.8133 29.3897 14.1814 29.089 14.1814 28.5444L14.1814 9.0857C14.1814 8.54114 14.8133 8.24037 15.236 8.58373L27.2122 18.3131Z" fill="black"/>
            </svg>
          </div>
        </div>
      )}

      {/* Show video player when thumbnail is clicked */}
      {inView && url && showPlayer && (
        <Suspense fallback={
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        }>
          <div 
            className={`absolute inset-0 w-full h-full ${isAudio ? 'opacity-0' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <LazyCustomVideoPlayer
              videoSrc={url}
              startTime={0}
              endTime={Infinity}
              autoPlay={true}
            />
          </div>
        </Suspense>
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50">
          <p className="text-sm text-white mb-3">Failed to load video</p>
          <button 
            onClick={handleRetry}
            className="flex items-center gap-2 bg-[#FF4A8E] text-white px-4 py-2 rounded-md hover:bg-[#E43D7A] transition-colors"
          >
            <svg className="ml-1" width="16" height="16" viewBox="0 0 37 38" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M27.2122 18.3131C27.5308 18.5719 27.5308 19.0582 27.2122 19.317L15.236 29.0464C14.8133 29.3897 14.1814 29.089 14.1814 28.5444L14.1814 9.0857C14.1814 8.54114 14.8133 8.24037 15.236 8.58373L27.2122 18.3131Z" fill="white"/>
            </svg>
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {!url && !error && inView && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  );
}; 